import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormLabelProps,
} from '@chakra-ui/react';
import {
  AsyncProps,
  AsyncSelect,
  ChakraStylesConfig,
  OptionBase,
} from 'chakra-react-select';
import { GroupBase } from 'chakra-react-select';
import { useMemo, useState } from 'react';
import {
  FieldError,
  FieldValues,
  get,
  Path,
  UseFormReturn,
} from 'react-hook-form';

export interface RecipeOptions extends OptionBase {
  value: string;
  label: string;
}

interface SelectRecipeFieldProps<
  Option extends RecipeOptions = RecipeOptions,
  Group extends GroupBase<Option> = GroupBase<Option>,
  FormValues extends FieldValues = FieldValues
> extends Omit<
    AsyncProps<Option, false, Group>,
    'name' | 'defaultValue' | 'form'
  > {
  form: UseFormReturn<FormValues>;
  name: Path<FormValues>;
  label?: string;
  labelProps?: FormLabelProps;
  handleOnSelect?: (val: Option) => void;
}

export function SelectSingleRecipeField<
  Option extends RecipeOptions,
  Group extends GroupBase<Option> = GroupBase<Option>,
  FormValues extends FieldValues = FieldValues
>({
  form,
  name,
  label,
  labelProps,
  handleOnSelect,
  ...props
}: SelectRecipeFieldProps<Option, Group, FormValues>) {
  const chakraStyles: ChakraStylesConfig<Option, false, Group> = useMemo(
    () => ({
      option: (provided, { isSelected }) => ({
        ...provided,
        ...(isSelected && {
          backgroundColor: 'azure',
          color: 'white',
        }),
      }),
    }),
    []
  );

  const {
    formState: { errors },
  } = form;

  const error: FieldError | undefined = get(errors, name);
  const isInvalid = !!error;
  const errorMessage = error?.message || '';

  const [selected, setSelected] = useState<Option | undefined>();

  return (
    <FormControl isInvalid={isInvalid}>
      {label && (
        <FormLabel aria-label={label} {...labelProps}>
          {label}
        </FormLabel>
      )}
      <AsyncSelect
        chakraStyles={chakraStyles}
        onChange={(option) => {
          if (option) {
            handleOnSelect?.(option);
            setSelected(option);
          }
        }}
        {...props}
        value={selected}
      />
      <FormErrorMessage textColor="red">
        {errorMessage as string}
      </FormErrorMessage>
    </FormControl>
  );
}
